import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, Paper, Typography,
} from '@mui/material';
import { getCountFromServer } from 'firebase/firestore';
import { seasonPlayersCollection } from '../../../services/players';
import { useSeason } from '../../../context/SeasonContext';
import { useNotification } from '../../../context/NotificationContext';
import { SEASON_STATUS, SEASON_STATUS_LABELS, fetchSubleaguesForSeason } from '../../../services/seasons';

const STATUS_CHIP_PROPS = {
    [SEASON_STATUS.ACTIEF]: { color: 'success', variant: 'filled' },
    [SEASON_STATUS.VOORBEREIDING]: { color: 'warning', variant: 'outlined' },
    [SEASON_STATUS.AFGEROND]: { color: 'default', variant: 'outlined' },
};

function StatCard({ label, value, hint }) {
    return (
        <Paper sx={{ p: 2.5, flex: 1, minWidth: 180 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{label}</Typography>
            <Typography variant="h4" component="div">{value}</Typography>
            {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
        </Paper>
    );
}

/**
 * Startscherm binnen een seizoen: waar staat dit seizoen, en welke subleagues
 * spelen erop. De weekelijkse stappenflow ("Nu aan de hand", aandachtspunten)
 * volgt in een latere fase, samen met het herontwerp van Kalender & rondes.
 */
function AdminSeasonDashboard() {
    const { season, seasonId } = useSeason();
    const notify = useNotification();
    const [subleagues, setSubleagues] = useState([]);
    const [playerCount, setPlayerCount] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            try {
                const [seasonSubleagues, playerSnapshot] = await Promise.all([
                    fetchSubleaguesForSeason(seasonId),
                    getCountFromServer(seasonPlayersCollection(seasonId)),
                ]);
                if (cancelled) return;
                setSubleagues(seasonSubleagues);
                setPlayerCount(playerSnapshot.data().count);
            } catch (err) {
                console.error('Kon seizoensoverzicht niet laden:', err);
                if (!cancelled) notify.error('Niet alle gegevens van dit seizoen konden geladen worden.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [seasonId, notify]);

    const participantCount = subleagues.reduce(
        (total, auction) => total + (auction.participantUids || []).length,
        0,
    );
    const chipProps = STATUS_CHIP_PROPS[season.status] || STATUS_CHIP_PROPS[SEASON_STATUS.AFGEROND];

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <Box component={RouterLink} to="/admin" sx={{ color: 'primary.main', textDecoration: 'none' }}>
                    Seizoenen
                </Box>
                {' › '}
                {season.displayName}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Typography variant="h4">{season.displayName}</Typography>
                <Chip size="small" label={SEASON_STATUS_LABELS[season.status]} {...chipProps} />
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                        <StatCard label="Subleagues" value={subleagues.length} />
                        <StatCard label="Deelnemers" value={participantCount} hint="over alle subleagues" />
                        <StatCard
                            label="Speelrondes"
                            value={season.roundCount}
                            hint={season.roundCount ? 'in de kalender' : 'kalender nog niet geïmporteerd'}
                        />
                        <StatCard label="Spelers" value={playerCount ?? '—'} hint="in de stamgegevens" />
                    </Box>

                    <Paper sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
                            <Typography variant="h6">Subleagues</Typography>
                            <Button component={RouterLink} to={`/admin/season/${seasonId}/subleagues`} size="small">
                                Alle {subleagues.length}
                            </Button>
                        </Box>

                        {subleagues.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                Er speelt nog geen enkele subleague op dit seizoen.
                            </Typography>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {subleagues.slice(0, 6).map((auction) => (
                                    <Box
                                        key={auction.id}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 2,
                                            p: 1.5, borderRadius: 1, backgroundColor: 'action.hover',
                                        }}
                                    >
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2">{auction.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {(auction.participantUids || []).length} deelnemers
                                            </Typography>
                                        </Box>
                                        {auction.status === 'Test' && (
                                            <Chip size="small" label="Test" variant="outlined" />
                                        )}
                                        <Chip
                                            size="small"
                                            label={auction.spelfase || 'Voorbereiding'}
                                            variant="outlined"
                                            color="primary"
                                        />
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Paper>
                </>
            )}
        </Box>
    );
}

export default AdminSeasonDashboard;
